import { useMemo, useRef } from 'react';

import { AGGREGATE_NAMES, USER_RESOLVERS, USER_STATES } from 'cpool-constants';
import { schemaUserResolver_GetAdvisersByRole } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';

import { useAggregateQuery } from '~/queryUtils/useQuery';

type FormattedAdviser = {
    buckets: Array<string>;
    id: string;
    isAssignable: boolean;
    name: string;
};

type AdviserAssign = {
    advisers: Array<FormattedAdviser>;
    // changeAdviser: (leadId: string, adviserId: string) => void;
    activeAdviser: string;
};

export function useAdviserAssign(
    currentAdviserId?: string,
    leadUserIds: string[] = [],
    // refreshLeadList?: (position?: number) => Promise<void>,
): AdviserAssign {
    const { t } = useTranslation();
    const queryPosition = useRef<number | undefined>(undefined);
    const adviserResult = useAggregateQuery({
        acl: { userId: currentAdviserId },
        aggregateName: AGGREGATE_NAMES.USER,
        resolverName: USER_RESOLVERS.getAdvisersByRole,
        schema: schemaUserResolver_GetAdvisersByRole,
        searchParams: {
            ...(leadUserIds.length ? { leadUserIds } : {}),
            position: queryPosition.current,
        },
    });

    const activeAdviser = useMemo(() => {
        const curAdviser = (adviserResult.data ?? []).find((cur) => cur.id === currentAdviserId);
        if (!curAdviser) {
            return t('LEADS.ADVISER.ASSIGN');
        }
        return `${curAdviser.firstName} ${curAdviser.lastName}`;
    }, [adviserResult.data, currentAdviserId, t]);

    const advisers = useMemo(() => {
        const adviserList = adviserResult.data ?? [];

        return adviserList
            .filter((adviser) => !adviser.nameOnly)
            .map((adviser) => ({
                buckets: adviser.buckets,
                id: adviser.id,
                isAssignable: adviser.state === USER_STATES.AVAILABLE && !adviser.absent,
                name: `${adviser.firstName} ${adviser.lastName}`,
            }))
            .filter((adviser) => adviser.id !== currentAdviserId);
    }, [adviserResult.data, currentAdviserId]);

    return { activeAdviser, advisers };
}
